import type { TabsContextType } from './TabsContext.ts';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { Bleed } from '../Bleed/index.ts';
import { TabsContext } from './TabsContext.ts';

export interface TabsProps<TValue = string> {
  bleed?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  heading?: React.ReactNode;
  onChange?: (value: TValue) => void;
  plain?: boolean;
  value?: TValue;
}

export function Tabs<TValue>({
  bleed = false,
  disabled = false,
  children,
  heading,
  onChange,
  plain = false,
  value
}: TabsProps<TValue>) {
  const setValue = useCallback(
    (tabValue: TValue) => {
      onChange?.(tabValue);
    },
    [onChange]
  );

  const TabsProvider = TabsContext.Provider as unknown as React.Context<
    TabsContextType<TValue>
  >['Provider'];

  return (
    <TabsProvider
      value={useMemo(
        () => ({ bleed, disabled, plain, setValue, value }),
        [bleed, disabled, plain, setValue, value]
      )}
    >
      <Bleed
        className={clsx('border-b border-gray-200', plain && 'pb-6')}
        horizontal
        mobileOnly={!bleed}
      >
        <div className={clsx(heading && 'sm:flex sm:items-baseline')}>
          {heading && (
            <h3 className={clsx('leading-6 font-semibold', plain ? 'text-base' : 'text-lg')}>
              {heading}
            </h3>
          )}
          <div
            className={clsx(
              '-mx-(--gutter-x)',
              !bleed && 'sm:mx-0',
              heading && [
                plain
                  ? 'mt-4 sm:mt-0 sm:ml-6 sm:border-l sm:border-gray-200 sm:pl-6'
                  : 'mt-4 sm:mt-0 sm:ml-10'
              ]
            )}
          >
            <div
              className="scrollbar-none overflow-x-scroll sm:overflow-x-visible"
              style={{
                WebkitOverflowScrolling: 'touch'
              }}
            >
              <nav
                className={clsx(
                  'px-(--gutter-x)',
                  !bleed && 'sm:px-0',
                  'flex min-w-min gap-x-8 sm:-mb-px'
                )}
              >
                {children}
              </nav>
            </div>
          </div>
        </div>
      </Bleed>
    </TabsProvider>
  );
}
